import { Dialog, DialogActions, DialogContent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Button } from '@/components/Button';
import { colorStore } from '@/features/queueColors';
import { WeeklyWorkLog } from '@/types';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { getTrackerUrl } from '@/utils/getTrackerUrl';
import { isMac } from '@/utils/isMac';

import s from './DeleteModal.module.scss';

type DeleteModalProps = {
  confirmDelete: () => void;
  cancelDelete: () => void;
  logToDelete: WeeklyWorkLog | null;
  isLoading?: boolean;
};

const DeleteModal: React.FC<DeleteModalProps> = ({
  logToDelete,
  confirmDelete,
  cancelDelete,
  isLoading = false,
}) => {
  useHotkeys(isMac ? 'meta+enter' : 'alt+enter', (e) => {
    e.preventDefault();
    confirmDelete();
  });

  if (!logToDelete) {
    return null;
  }

  const keyPrefix = logToDelete.taskKey.split('-')[0];
  const color = colorStore.getColorByPrefix(keyPrefix);
  const needsBorder = colorStore.needsBorder(keyPrefix);

  return (
    <Dialog
      sx={{ '.MuiPaper-root': { width: '25%', borderRadius: '8px' } }}
      open={Boolean(logToDelete)}
      onClose={cancelDelete}
    >
      <h1 className={s.title}>Точно удалить запись?</h1>

      <DialogContent>
        <div className={s.card}>
          <div className={s.card__line} style={{ backgroundColor: color }} />
          <div className={s.card__body}>
            <div className={s.card__title}>
              <h2>
                <a
                  href={getTrackerUrl(logToDelete.taskKey)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color,
                    ...(needsBorder && {
                      textShadow: '0 0 1px rgba(0, 0, 0, 0.5), 0 0 1px rgba(0, 0, 0, 0.5)',
                    }),
                  }}
                >
                  {logToDelete.taskKey}
                </a>
              </h2>
              {logToDelete.durationMinutes && (
                <span>{formatDurationRussian(logToDelete.durationMinutes)}</span>
              )}
            </div>
            <h2>{logToDelete.taskTitle}</h2>
            <div className={s.card__body_comment}>
              <pre>{logToDelete.comment}</pre>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <div className={s.buttons}>
          <Button
            sx={{
              width: '45%',
              borderRadius: '8px',
            }}
            onClick={cancelDelete}
            variant="outlined"
            color="primary"
            disabled={isLoading}
          >
            <p>Отмена</p>
          </Button>
          <Button
            sx={{
              width: '45%',
              borderRadius: '8px',
              backgroundColor: 'var(--danger-color)',
              '&:hover': {
                backgroundColor: 'var(--danger-hover-color)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'var(--danger-color)',
                opacity: 0.6,
                cursor: 'not-allowed',
              },
            }}
            disabled={isLoading}
            onClick={confirmDelete}
            variant="contained"
          >
            <p>Удалить</p>
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default observer(DeleteModal);
